import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import CustomStringLink from '../../common/customStringLink';
import UrlConstants from '../../../constants/UrlConstants';

interface ReportHeadersPivotedListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    month: LanguageUtils.createMessage('Month'),
    year: LanguageUtils.createMessage('Year'),
    notReceived: LanguageUtils.createMessage('Not received'),
    needHandling: LanguageUtils.createMessage('Need handling'),
    ready: LanguageUtils.createMessage('ready'),
    invoiced: LanguageUtils.createMessage('Invoiced')
};

const ReportHeadersPivotedList = forwardRef((props: ReportHeadersPivotedListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_KICKBACK_POSTEN);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'departureYear',
                headerName: `${intl.formatMessage(messages.year)}`,
                flex: 1,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.departureYear}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('departureYear', hiddenColumns)
            },
            {
                field: 'departureMonth',
                headerName: intl.formatMessage(messages.month),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.departureMonth}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('departureMonth', hiddenColumns)
            },
            {
                field: 'notReceived',
                headerName: intl.formatMessage(messages.notReceived),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.notReceived}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('notReceived', hiddenColumns)
            },
            {
                field: 'invoiced',
                headerName: intl.formatMessage(messages.invoiced),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiced}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiced', hiddenColumns)
            },
            {
                field: 'needHandling',
                headerName: intl.formatMessage(messages.needHandling),
                flex: 1,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.needHandling}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('needHandling', hiddenColumns)
            },
            {
                field: 'ready',
                headerName: intl.formatMessage(messages.ready),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.ready}
                        link={`/#/${UrlConstants.REPORT_HEADER}/${applyRowFn(param)?.departureMonth}/${applyRowFn(param)?.departureYear}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('ready', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.id + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default ReportHeadersPivotedList;
